import classNames from 'classnames';
import { ReactNode } from 'react';
import { AiOutlineClockCircle } from 'react-icons/ai';

type AlertType = 'warning' | 'error';

export interface IPropsAlert {
  type?: AlertType;
  message?: string;
  description?: string | string[];
  showIcon?: boolean;
  action?: ReactNode;
}

const defaultProps = {
  message: '',
  description: '',
  type: 'warning',
  showIcon: false,
  action: undefined,
};

const ALERT_COLOR_SCHEME = {
  // Own theme-aware tokens, not daisyUI's fixed `alert-warning` — that yellow never adapts to
  // dark mode (same saturated hex in both themes), which is what made it glare. These tokens
  // already carry separate, tuned day/night values (see ColorSchemeCSSVariable).
  warning: {
    backgroundColor: 'bg-colorsBrandOrange08FillQuaternary',
    textColor: 'text-colorsBrandWarmBlack02LabelSecondary',
    accentColor: 'text-colorsBrandOrange01Primary',
  },
  error: {
    backgroundColor: 'alert alert-error shadow-lg',
    textColor: 'text-white',
    accentColor: 'text-white',
  },
};

const Alert = (props: IPropsAlert) => {
  const { type, message, description, showIcon, action } = props;
  const colorScheme = ALERT_COLOR_SCHEME[type as AlertType];

  return (
    <div className={classNames('flex w-full items-center justify-between rounded-6 px-16 py-12', colorScheme.backgroundColor)}>
      <div className="flex flex-col items-start">
        <div className="flex items-center">
          {showIcon && <AiOutlineClockCircle name="clock" size="24px" className={colorScheme.accentColor} />}
          <span className={classNames('ml-4 font-semibold font-opn text-16', colorScheme.accentColor)}>{message}</span>
        </div>
        {description && (
          <div className={classNames('mt-4 font-tt text-20', colorScheme.textColor)}>
            {typeof description === 'string' ? (
              description
            ) : (
              <ul className="leading-tight">
                {description.map((_desc) => (
                  <li key={_desc} className="before:mr-4 before:content-['-']">
                    {_desc}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      {action && <div>{action}</div>}
    </div>
  );
};

Alert.defaultProps = defaultProps;

export default Alert;
